import { Draft } from 'immer';
import { camelCase, identity, upperFirst } from 'lodash';
import { FC, memo, useContext, useEffect } from 'react';
import { useImmer } from 'use-immer';

import { useStorage } from '@gmm/ui';

import { createNamedContext } from './createNamedContext';
import { useTeacherStorage } from './hooks/useTeacherLocalStorage';

const pascalCase = (name: string): string => upperFirst(camelCase(name));

const makeErrorMessage = (
  hook: { displayName: string },
  provider: { displayName?: string },
): string =>
  `${hook.displayName} should only be used within ${provider.displayName}`;

export interface StateProviderProps<T> {
  /**
   * Allows overriding of the passed `initialState` used to create the
   * store. Mostly useful for testing, but could come in handy.
   */
  initialState?: Partial<T>;
}

type StoreProvider<T> = FC<StateProviderProps<T>>;
type UseSetState<T> = (() => (
  f: (draft: (T & Partial<T>) | Draft<T & Partial<T>>) => void,
) => void) & {
  displayName: string;
};
type UseStore<T> = (() => T) & { displayName: string };

interface MakeStoreOptions<T> {
  /** The name of the store */
  name: string;
  /** The initial state of the store */
  initialState: T;
  /** The key to use to store state in localStorage */
  localStorageKey?: string;
  /** Whether we should use `useTeacherLocalStorage` or just `useLocalStorage` */
  scopeToTeacher?: boolean;
  /** Defaults to localStorage */
  storage?: Storage;
  /**
   * Function to modify what gets stored in localStorage.
   * Useful when you want to omit a property. Will store entire state by default
   */
  storageTransform?: (state: T) => Partial<T>;
}

export function makeStore<T>({
  name,
  initialState,
  localStorageKey,
  scopeToTeacher,
  storage,
  storageTransform = identity,
}: MakeStoreOptions<T>): [StoreProvider<T>, UseSetState<T>, UseStore<T>] {
  const sharedName = pascalCase(name);
  const SetStateContext = createNamedContext<
    (f: (draft: (T & Partial<T>) | Draft<T & Partial<T>>) => void) => void
  >(`${sharedName}StateContext`, null as any);
  const StoreContext = createNamedContext<T>(
    `${sharedName}StoreContext`,
    null as any,
  );
  const useStoreStorage = scopeToTeacher ? useTeacherStorage : useStorage;

  const StoreProvider: StoreProvider<T> = ({
    children,
    initialState: overrides,
  }) => {
    const [storedState, setStoredState] = useStoreStorage<Partial<T>>(
      localStorageKey ?? '',
      { ...initialState, ...overrides },
      storage,
    );
    const [state, setState] = useImmer({
      ...initialState,
      ...storedState,
    });

    useEffect(() => {
      if (!localStorageKey) return;

      setStoredState(storageTransform(state));
    }, [setStoredState, state]);

    return (
      <SetStateContext.Provider value={setState}>
        <StoreContext.Provider value={state}>{children}</StoreContext.Provider>
      </SetStateContext.Provider>
    );
  };

  if (process.env.NODE_ENV !== 'production') {
    StoreProvider.displayName = `${sharedName}Provider`;
  }

  const useSetState: UseSetState<T> = () => {
    const setStateValue = useContext(SetStateContext);

    if (process.env.NODE_ENV === 'development' && setStateValue === null) {
      throw new Error(makeErrorMessage(useSetState, StoreProvider));
    }

    return setStateValue;
  };

  if (process.env.NODE_ENV !== 'production') {
    useSetState.displayName = `use${sharedName}SetState`;
  }

  const useStore: UseStore<T> = () => {
    const storeValue = useContext(StoreContext);

    if (process.env.NODE_ENV === 'development' && storeValue === null) {
      throw new Error(makeErrorMessage(useStore, StoreProvider));
    }

    return storeValue;
  };

  if (process.env.NODE_ENV !== 'production') {
    useStore.displayName = `use${sharedName}Store`;
  }

  return [memo(StoreProvider), useSetState, useStore];
}
